import { useEffect, useMemo, useState } from 'react'
import { BarChart3, Loader2, Star, MessageCircle, Users, AlertTriangle, AlertCircle, Eye, EyeOff, Trash2 } from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD } from '../components/ui.jsx'
import SurveyPanel from './_OperationSurveys.jsx'

/**
 * CST scores tab — per-operation summary view.
 *
 * Shows the conclusions of every CST response collected for THIS operation
 * only. Side panel of the main /cst page (which aggregates across every op).
 *
 * Layout:
 *   1. Overview cards: total responses, overall mean, promoter share,
 *      comment count
 *   2. Per-question card: each Q with its mean + a 1→5 distribution bar
 *   3. Recent comments: last suggestions (limited preview)
 */

// Mirrors functions/api/survey/cst/[token].js → CST_QUESTIONS — kept here
// so the tab doesn't have to do a second round-trip just for label text.
const CST_QUESTIONS = [
  'ขั้นตอนลงทะเบียนก่อนรับการรักษา สะดวกรวดเร็ว ไม่ยุ่งยาก',
  'เจ้าหน้าที่เป็นมิตร และให้คำแนะนำที่ดี',
  'คุณพึงพอใจกับการดูแลรักษาที่ได้รับจากคุณหมอ',
  'รถทันตกรรมสะอาดสะอ้าน รู้สึกมั่นใจในมาตรฐานการฆ่าเชื้อ',
  'โดยรวมแล้วท่านรู้สึกประทับใจในการใช้บริการรถทันตกรรมเคลื่อนที่ Tooth Taxi',
  'ท่านจะแนะนำบริการของ Tooth Taxi ให้กับผู้อื่น หรืออยากใช้บริการอีกครั้งในโอกาสหน้า',
]

export default function CstTab({ op }) {
  const [data, setData] = useState(null)
  const [err, setErr]   = useState(null)
  const [tick, bump]    = useState(0)

  useEffect(() => {
    let cancelled = false
    setData(null); setErr(null)
    api.operations.surveys.cstResponses(op.id)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op.id, tick])

  const stats = useMemo(() => {
    if (!data) return null
    const rows = data.rows || []
    if (!rows.length) return { count: 0 }
    const perQ = [1, 2, 3, 4, 5, 6].map((qi) => {
      const key = `q${qi}`
      const vals = rows.map((r) => r[key])
      const sum = vals.reduce((a, b) => a + b, 0)
      const dist = [1, 2, 3, 4, 5].map((s) => vals.filter((v) => v === s).length)
      return { qi, mean: sum / vals.length, dist }
    })
    const overall = perQ.reduce((acc, q) => acc + q.mean, 0) / 6

    // "Lowest-scoring question" — diagnostic signal pointing at the weakest
    // touchpoint so staff knows what to improve.
    const lowest = perQ.reduce((min, q) => q.mean < min.mean ? q : min, perQ[0])

    const comments = rows.filter((r) => (r.suggestion || '').trim()).map((r) => ({
      id: r.id,
      text: r.suggestion.trim(),
      createdAt: r.created_at,
      mean: (r.q1 + r.q2 + r.q3 + r.q4 + r.q5 + r.q6) / 6,
      hidden: !!r.hidden,
    }))
    return {
      count: rows.length, overall, perQ,
      lowest, commentCount: comments.length,
      comments,
    }
  }, [data])

  if (err) {
    return (
      <div className={`${CARD} p-5 text-rose-700 text-[13px] inline-flex items-center gap-2`}>
        <AlertTriangle size={14} /> {err}
      </div>
    )
  }
  if (!data) {
    return (
      <div className={`${CARD} p-8 text-center text-zinc-500 text-[13px]`}>
        <Loader2 size={16} className="inline animate-spin mr-1.5" /> Loading…
      </div>
    )
  }
  if (stats.count === 0) {
    return (
      <div className="space-y-4">
        <SurveyPanel opId={op.id} op={op} cards={['cst']} />
        <div className={`${CARD} p-8 text-center`}>
          <BarChart3 size={26} className="mx-auto text-zinc-300 mb-2" />
          <p className="text-[13.5px] text-zinc-700 font-medium">ยังไม่มี CST response</p>
          <p className="text-[12px] text-zinc-500 mt-1">สร้างลิงก์ CST ด้านบน แล้วส่งให้คนไข้กรอกหลังจบบริการ</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* CST link + revoke + count (moved here from PR media).
          refreshKey/onChange keep the responses panel, the CST mean card, and
          the per-question stats below in sync when a response is hidden/deleted. */}
      <SurveyPanel opId={op.id} op={op} cards={['cst']}
        refreshKey={tick} onChange={() => bump((n) => n + 1)} />
      <OverviewGrid s={stats} />
      <PerQuestion s={stats} />
      <CommentsList comments={stats.comments} opId={op.id} onChange={() => bump((n) => n + 1)} />
    </div>
  )
}


function OverviewGrid({ s }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard
        icon={<Star  size={14} fill="currentColor" />}
        label="คะแนนเฉลี่ย"
        value={s.overall.toFixed(2)}
        sub="เต็ม 5.00"
        tone={toneFor(s.overall)}
      />
      <StatCard
        icon={<Users size={14} />}
        label="จำนวนผู้ตอบ"
        value={s.count.toLocaleString()}
        sub="ราย"
      />
      <StatCard
        icon={<AlertCircle size={14} />}
        label="ข้อที่ต่ำสุด"
        value={s.lowest.mean.toFixed(2)}
        sub={`Q${s.lowest.qi}: ${CST_QUESTIONS[s.lowest.qi - 1]}`}
        tone={toneFor(s.lowest.mean)}
      />
      <StatCard
        icon={<MessageCircle size={14} />}
        label="ข้อเสนอแนะ"
        value={s.commentCount.toLocaleString()}
        sub={`${Math.round((s.commentCount / s.count) * 100)}% ของผู้ตอบ`}
      />
    </div>
  )
}

function StatCard({ icon, label, value, sub, tone }) {
  const toneCls = tone === 'emerald'
    ? 'bg-emerald-50 ring-emerald-200 text-emerald-800'
    : tone === 'amber'
      ? 'bg-amber-50 ring-amber-200 text-amber-800'
      : tone === 'rose'
        ? 'bg-rose-50 ring-rose-200 text-rose-800'
        : 'bg-white ring-zinc-200 text-zinc-800'
  return (
    <div className={`rounded-xl ring-1 px-4 py-3 ${toneCls}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold opacity-70">
        {icon} <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-[22px] font-bold tabular-nums leading-tight">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5 line-clamp-2 leading-snug" title={sub}>{sub}</div>}
    </div>
  )
}

function PerQuestion({ s }) {
  return (
    <div className={`${CARD} p-5`}>
      <h3 className="text-[14px] font-semibold text-zinc-900 mb-3">คะแนนรายข้อ</h3>
      <ul className="divide-y divide-zinc-100">
        {CST_QUESTIONS.map((q, i) => (
          <QuestionRow key={i} n={i + 1} text={q} mean={s.perQ[i].mean} dist={s.perQ[i].dist} total={s.count} />
        ))}
      </ul>
    </div>
  )
}

function QuestionRow({ n, text, mean, dist, total }) {
  const tone = toneFor(mean)
  const meanColor = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-rose-700'
  const barColor  = tone === 'emerald' ? 'bg-emerald-500'   : tone === 'amber' ? 'bg-amber-400'  : 'bg-rose-400'

  // Tooltip lists the per-star distribution without taking up visual real
  // estate. Only built when there's >1 non-zero bucket (otherwise the score
  // is unanimous and the breakdown is uninformative).
  const distTip = dist.map((c, i) => c ? `${i + 1}★ ${c}` : null).filter(Boolean).join(' · ')

  return (
    <li className="py-3 first:pt-1 last:pb-1">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10.5px] text-zinc-400 font-semibold tabular-nums shrink-0">Q{n}</span>
          <span className="text-[13px] text-zinc-800 leading-snug">{text}</span>
        </div>
        <div className="shrink-0 flex items-center gap-2.5">
          <Stars value={mean} />
          <span className={`text-[14px] font-bold tabular-nums w-10 text-right ${meanColor}`}>
            {mean.toFixed(2)}
          </span>
        </div>
      </div>
      {/* Single progress bar — width = mean/5 — for visual weight without
          the busy 5-color stacked bar. Tooltip carries the distribution. */}
      <div className="mt-1.5 ml-6 h-1.5 rounded-full bg-zinc-100 overflow-hidden" title={distTip || `${total} response`}>
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.max(2, (mean / 5) * 100)}%` }} />
      </div>
    </li>
  )
}

/** 5-star summary using filled / half / empty glyphs.
 *  Rounds to nearest half star (4.25 → 4 stars + 1 half-empty). */
function Stars({ value }) {
  const v = Math.max(0, Math.min(5, value))
  const rounded = Math.round(v * 2) / 2   // 0, 0.5, 1.0, …, 5.0
  const full = Math.floor(rounded)
  const hasHalf = rounded - full === 0.5
  const tone = toneFor(value)
  const filled  = tone === 'emerald' ? 'text-emerald-500' : tone === 'amber' ? 'text-amber-500' : 'text-rose-500'
  const empty   = 'text-zinc-200'
  return (
    <span className="inline-flex items-center gap-px">
      {[0, 1, 2, 3, 4].map((i) => {
        if (i < full) return <Star key={i} size={11} className={filled} fill="currentColor" strokeWidth={0} />
        if (i === full && hasHalf) {
          // Half star — render two overlapping Stars, the right half empty.
          return (
            <span key={i} className="relative inline-block w-[11px] h-[11px]">
              <Star size={11} className={empty}  fill="currentColor" strokeWidth={0} />
              <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                <Star size={11} className={filled} fill="currentColor" strokeWidth={0} />
              </span>
            </span>
          )
        }
        return <Star key={i} size={11} className={empty} fill="currentColor" strokeWidth={0} />
      })}
    </span>
  )
}

function CommentsList({ comments, opId, onChange }) {
  if (!comments.length) return null
  const visibleCount = comments.filter((c) => !c.hidden).length
  const hiddenCount  = comments.length - visibleCount
  return (
    <div className={`${CARD} p-5`}>
      <h3 className="text-[14px] font-semibold text-zinc-900 mb-3 inline-flex items-center gap-2 flex-wrap">
        <MessageCircle size={14} className="text-amber-500" /> ข้อเสนอแนะจากผู้ใช้บริการ
        <span className="text-[11px] text-zinc-400 font-normal">({comments.length})</span>
        {hiddenCount > 0 && (
          <span className="text-[10.5px] text-zinc-500 bg-zinc-100 ring-1 ring-zinc-200 px-1.5 py-0.5 rounded">
            ซ่อนจาก HR {hiddenCount}
          </span>
        )}
      </h3>
      <p className="text-[11.5px] text-zinc-400 mb-2 leading-snug">
        <EyeOff size={10} className="inline -mt-0.5 mr-0.5" /> ซ่อน = ตัด response นี้ออกจากรายงานของ HR ทั้งหมด (ทั้งคะแนนและข้อความ) แต่ยังเห็นในหน้านี้ ·
        <Trash2 size={10} className="inline -mt-0.5 mx-0.5 text-rose-400" /> ลบ = ลบถาวรทุกที่ ย้อนกลับไม่ได้
      </p>
      <ul className="space-y-2">
        {comments.map((c) => (
          <CommentRow key={c.id} comment={c} opId={opId} onChange={onChange} />
        ))}
      </ul>
    </div>
  )
}

function CommentRow({ comment: c, opId, onChange }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)
  const [confirming, setConfirming] = useState(false)

  async function toggle() {
    setBusy(true); setErr(null)
    try {
      await api.operations.surveys.updateCstResponse(opId, c.id, { hidden: !c.hidden })
      onChange()
    } catch (e) { setErr(e?.message || 'error'); setBusy(false) }
  }

  async function remove() {
    setBusy(true); setErr(null)
    try {
      await api.operations.surveys.deleteCstResponse(opId, c.id)
      onChange()
    } catch (e) { setErr(e?.message || 'error'); setBusy(false); setConfirming(false) }
  }

  return (
    <li className={'rounded-lg ring-1 px-3 py-2.5 transition ' +
      (c.hidden
        ? 'ring-zinc-200 bg-zinc-100/60 opacity-70'
        : 'ring-zinc-200 bg-zinc-50/40')}>
      <div className="flex items-start justify-between gap-2 mb-0.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full font-medium ${toneClsFor(c.mean)}`}>
            คะแนนเฉลี่ย {c.mean.toFixed(2)}
          </span>
          {c.hidden && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-zinc-200 text-zinc-600 inline-flex items-center gap-1">
              <EyeOff size={9} /> ซ่อนจาก HR
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10.5px] text-zinc-400 tabular-nums">{fmtDateTime(c.createdAt)}</span>
          {confirming ? (
            <span className="inline-flex items-center gap-1">
              <button onClick={remove} disabled={busy}
                className="px-2 h-7 rounded-md text-[11px] font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} ลบถาวร
              </button>
              <button onClick={() => setConfirming(false)} disabled={busy}
                className="px-2 h-7 rounded-md text-[11px] text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-100 disabled:opacity-50">
                ยกเลิก
              </button>
            </span>
          ) : (
            <>
              <button onClick={toggle} disabled={busy} title={c.hidden ? 'แสดงให้ HR เห็น' : 'ซ่อนจาก HR'}
                className={'w-7 h-7 rounded-md inline-flex items-center justify-center transition disabled:opacity-50 ' +
                  (c.hidden
                    ? 'text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50'
                    : 'text-zinc-400 hover:text-amber-700 hover:bg-amber-50')}>
                {busy ? <Loader2 size={11} className="animate-spin" />
                  : c.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button onClick={() => setConfirming(true)} disabled={busy} title="ลบ response นี้ถาวร"
                className="w-7 h-7 rounded-md inline-flex items-center justify-center text-zinc-400 hover:text-rose-700 hover:bg-rose-50 transition disabled:opacity-50">
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
      </div>
      <p className={'text-[12.5px] whitespace-pre-wrap leading-snug ' +
        (c.hidden ? 'text-zinc-500 line-through decoration-zinc-300' : 'text-zinc-700')}>{c.text}</p>
      {err && (
        <div className="mt-1 text-[11px] text-rose-600 inline-flex items-center gap-1">
          <AlertTriangle size={10} /> {err}
        </div>
      )}
    </li>
  )
}


function toneFor(mean) {
  if (mean >= 4) return 'emerald'
  if (mean >= 3) return 'amber'
  return 'rose'
}
function toneClsFor(mean) {
  const t = toneFor(mean)
  return t === 'emerald' ? 'bg-emerald-50 text-emerald-700'
       : t === 'amber'   ? 'bg-amber-50 text-amber-700'
       :                   'bg-rose-50 text-rose-700'
}
function fmtDateTime(unixSeconds) {
  if (!unixSeconds) return ''
  const d = new Date(unixSeconds * 1000)
  return d.toLocaleString('th-TH-u-ca-gregory', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok',
  })
}
